import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import uuidv4 from 'uuidv4';
import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import TableChartIcon from '@material-ui/icons/TableChart';
import RemoveIcon from '@material-ui/icons/Remove';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Tooltip from '@material-ui/core/Tooltip';
import CardHeader from '@material-ui/core/CardHeader';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { makeStyles } from '@material-ui/core/styles';
import useSnack from 'hooks/useSnack';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { Formik, Field, FieldArray } from 'formik';
import TextField from 'components/form/Formik/FormikInput';
import FormikRadioGroup from 'components/form/Formik/FormikRadioGroup';
import Select from 'components/form/Formik/FormikReactSelect';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import { BLOCK_TYPES } from 'constant/form-builder';
import {
  ADD_EXERCISE,
  UPDATE_EXERCISE,
  GET_EXERCISE,
  GET_MODULE,
} from 'graphql/admin/curriculum';
import withConfirm from 'hooks/withConfirm';
import Blocks from './components/form-builder/Blocks';
import PreviewModal from './components/PreviewModal';
import { isEqual } from 'lodash';
import NavigationPrompt from 'react-router-navigation-prompt';
import CustomPrompt from 'components/common/CustomPrompt';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 800,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    bottom: 30,
    right: 30,
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 0,
    boxShadow: 'none',
    border: '1px solid #eee',
  },
  addAction: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
}));

const MENU_OPTIONS = [
  {
    label: 'Add Rich Text Content',
    Icon: TextFieldsIcon,
    type: BLOCK_TYPES.TITLE,
  },
  {
    label: 'Add a Question',
    Icon: QuestionAnswerIcon,
    type: BLOCK_TYPES.QUESTION,
  },
  {
    label: 'Add Table Layout',
    Icon: TableChartIcon,
    type: BLOCK_TYPES.QUESTIONS_TABLE,
  },
  {
    label: 'Add Page Break',
    Icon: RemoveIcon,
    type: BLOCK_TYPES.DIVIDER,
  },
];

const TAGS = ['Recommended', 'Optional', 'Pre-Work', 'Post-Work', 'Live Session'];

const required = (value) => (!value ? 'Required' : null);

const Builder = ({
  history,
  match,
  match: {
    params: { id, moduleId },
    url,
  },
  confirm,
}) => {
  const classes = useStyles();
  const snack = useSnack();
  const { data } = useQuery(GET_MODULE, { variables: { id: moduleId } });
  const [showPreview, togglePreview] = useState(false);
  const [exercise, setExercise] = useState({ type: 'worksheet', blocks: [] });
  const [addExercise, { loading: adding }] = useMutation(ADD_EXERCISE);
  const [updateExercise, { loading: updating }] = useMutation(UPDATE_EXERCISE);
  const client = useApolloClient();

  useEffect(() => {
    if (id) {
      client
        .query({
          query: GET_EXERCISE,
          variables: {
            id,
          },
        })
        .then(
          ({
            data: {
              exercises: [ex],
            },
          }) => {
            delete ex.__typename;
            setExercise(ex);
          },
        );
    }
  }, [id]);

  const handleSubmit = (values, { setSubmitting }) => {
    if (id) {
      updateExercise({
        variables: {
          id,
          input: values,
        },
      })
        .then(() => {
          snack.success('Exercise saved.');
        })
        .catch((e) => snack.error(e.message));
    } else {
      addExercise({
        variables: {
          input: {
            ...values,
            module_id: moduleId,
          },
        },
      })
        .then(
          ({
            data: {
              exercises: { returning },
            },
          }) => {
            setSubmitting(false);
            snack.success('Exercise saved.');
            const ex = returning[0];
            history.replace(`/curriculum/${moduleId}/exercises/${ex.id}`);
          },
        )
        .catch((e) => snack.error(e.message));
    }
  };

  const onDelete = () =>
    confirm(
      async () => {
        await updateExercise({
          variables: {
            id,
            input: {
              deleted: true,
            },
          },
        });
        snack.success('Exercise deleted.');
        history.goBack();
      },
      {
        description: 'Are you sure you want to delete this exercise?',
      },
    );

  return (
    <div className={classes.root}>
      <Helmet title={exercise?.name} />
      {id && !exercise.id ? (
        <Spin spinning />
      ) : (
        <Formik
          onSubmit={handleSubmit}
          initialValues={{
            blocks: exercise.blocks,
            name: exercise.name,
            description: exercise.description,
            duration: exercise.duration,
            type: exercise.type,
            meeting_type: exercise.meeting_type,
            tags: exercise.tags,
          }}
        >
          {({ values, handleSubmit, setValues }) => {
            const onMenuSelect = (type, popupState) => {
              values.blocks.push({
                id: uuidv4(),
                type,
              });
              setValues(values);
              popupState.close();
            };

            return (
              <React.Fragment>
                <Card className={classes.card}>
                  <CardHeader
                    title="Build exercise"
                    subheader="Add questions, rich text contents with images, links etc, move around sections by drag and drop."
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item md={8}>
                        <Field
                          fullWidth
                          name="name"
                          placeholder="Exercise name"
                          component={TextField}
                          validate={required}
                        />
                      </Grid>
                      <Grid item md={4}>
                        <Field
                          fullWidth
                          component={TextField}
                          validate={required}
                          name="duration"
                          placeholder="Duration (minutes)"
                          type="number"
                        />
                      </Grid>
                    </Grid>
                    <Field
                      fullWidth
                      component={TextField}
                      validate={required}
                      margin="normal"
                      name="description"
                      multiline
                      rows={3}
                      rowsMin={3}
                      placeholder="Exercise introduction"
                    />
                    <Typography variant="body2">Exercise type:</Typography>
                    <Field
                      row
                      validate={required}
                      name="type"
                      options={[
                        { label: 'Worksheet', value: 'worksheet' },
                        { label: '360 Snapshot', value: 'snapshot' },
                        { label: 'Meeting', value: 'meeting' },
                      ]}
                      component={FormikRadioGroup}
                    />
                    {values.type === 'meeting' && (
                      <div>
                        <Typography variant="body2">Meeting type:</Typography>
                        <Field
                          row
                          validate={required}
                          name="meeting_type"
                          options={[
                            { label: 'College Coach', value: 'cc' },
                            { label: 'Head Counselor', value: 'hc' },
                            { label: 'Financial Counselor', value: 'fa' },
                            {
                              label: 'Unity College Coach',
                              value: 'circle_cc',
                            },
                            {
                              label: 'Unity Head Counselor',
                              value: 'circle_hc',
                            },
                            { label: 'FAFSA', value: 'fafsa' },
                            { label: 'CSS Profile ', value: 'css_profile' },
                            {
                              label: 'FAFSA + CSS Profile',
                              value: 'fafsa_css',
                            },
                          ]}
                          component={FormikRadioGroup}
                        />
                      </div>
                    )}
                    <Field
                      isClearable
                      transformValue
                      isMulti={false}
                      name="tags"
                      placeholder="Select Tag"
                      component={Select}
                      options={TAGS.map((i) => ({ label: i, value: i }))}
                    />
                  </CardContent>
                </Card>
                {values.type === 'meeting' ? null : (
                  <React.Fragment>
                    <FieldArray name="blocks" component={Blocks} />
                    <div className={classes.addAction}>
                      <PopupState variant="popover">
                        {(popupState) => (
                          <React.Fragment>
                            <Button
                              {...bindTrigger(popupState)}
                              color="primary"
                              variant="outlined"
                              startIcon={<AddIcon />}
                            >
                              Add Section
                            </Button>
                            <Menu {...bindMenu(popupState)}>
                              {MENU_OPTIONS.map((i, idx) => (
                                <MenuItem
                                  key={idx}
                                  onClick={() =>
                                    onMenuSelect(i.type, popupState, values)
                                  }
                                >
                                  <ListItemIcon>
                                    <i.Icon fontSize="small" />
                                  </ListItemIcon>
                                  <Typography variant="inherit">
                                    {i.label}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Menu>
                          </React.Fragment>
                        )}
                      </PopupState>
                    </div>
                  </React.Fragment>
                )}
                <div className={classes.footer}>
                  {id && (
                    <Fab size="large" color="secondary" onClick={onDelete}>
                      <DeleteIcon />
                    </Fab>
                  )}
                  <br />
                  {values.type === 'meeting' ? null : (
                    <Tooltip title="Preview" placement="left">
                      <Fab onClick={() => togglePreview(true)}>
                        <VisibilityIcon />
                      </Fab>
                    </Tooltip>
                  )}
                  <br />
                  <Tooltip title="Save" placement="left">
                    <Fab
                      size="large"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={adding || updating}
                    >
                      <SaveIcon />
                    </Fab>
                  </Tooltip>
                </div>
                {showPreview && (
                  <PreviewModal
                    module={data && data.modules[0]}
                    data={values}
                    onClose={() => togglePreview(false)}
                  />
                )}
                <NavigationPrompt
                  when={
                    (!isEqual(exercise?.blocks, values?.blocks) ||
                      !isEqual(exercise?.name, values?.name) ||
                      !isEqual(exercise?.type, values?.type)) &&
                    !url.endsWith('add')
                  }
                >
                  {({ onConfirm, onCancel }) => (
                    <CustomPrompt
                      when={true}
                      message={JSON.stringify({
                        title: 'Are you sure you want to leave?',
                        description:
                          'Make sure you save any changes first, so you don’t lose what you’ve modified.',
                        confirmationText: "Don't Save",
                        confirmButtonProps: {
                          color: 'secondary',
                        },
                        cancellationText: 'Close',
                      })}
                      onCancel={onCancel}
                      onConfirm={onConfirm}
                    />
                  )}
                </NavigationPrompt>
              </React.Fragment>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default withConfirm(Builder);
